// @flow
import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Grid, Row, Col } from 'react-flexbox-grid';
import {
  MdClose,
  MdDirections,
  MdSearch,
} from 'react-icons/lib/md';
import {
  reduxForm,
  Field,
} from 'redux-form';

import i18n from 'i18n-js';
import * as selectors from '../../reducers';
import {
  GOING_ROUTE_TYPE,
  RETURNING_ROUTE_TYPE,
} from '../../types/routes';
import { OMNISEARCH_RESULT_TYPES } from '../Omnisearch';
import {
  onSubmit,
  validate,
} from './utils';
import PopupHeader from '../PopupHeader';
import PopupHeaderRightGroup from '../PopupHeaderRightGroup';
import PopupHeaderLeftGroup from '../PopupHeaderLeftGroup';
import PopupContent from '../PopupContent';
import PopupFooter from '../PopupFooter';
import PopupHeaderTitle from '../PopupHeaderTitle';
import Button from '../Button';
import PopupCloseButton from '../PopupCloseButton';
import {
  RenderInput,
  RenderTimePicker,
  RenderMultiSelect,
  RenderSelect,
  RenderTypeahead,
} from '../FieldRenderer';
import OmnisearchLoadingResults from '../OmnisearchLoadingResults';
import OmnisearchResult from '../OmnisearchResult';
import { generateFetcher } from '../Omnisearch/utils';
import CloseableInputSearchResult from '../CloseableInputSearchResult';

import {
  Cancel, CreateButton, Search,
} from '../CommonTexts';
import styles from './createRoutePopupForm.css';
import { getUserImage } from '../../utils';


type CreateRoutePopupFormPropTypes = {
  handleSubmit: Function,
  fetchDrivers: string => Promise<Array<Object>>,
  fetchMonitors: string => Promise<Array<Object>>,
  fetchBuses: string => Promise<Array<Object>>,
};

const DISABLED_WEEK_DAYS = [5, 6];

const customRenderSelected = (item, onClear) => {
  switch (item.result_type) {
    case OMNISEARCH_RESULT_TYPES.bus: {
      return (
        <CloseableInputSearchResult
          title={item.code_name}
          subtitle={item.plates}
          closeAction={onClear}
        />
      );
    }
    case OMNISEARCH_RESULT_TYPES.busDriver: {
      return (
        <CloseableInputSearchResult
          title={`${item.first_name} ${item.last_name}`}
          subtitle={`${item.license_type} ${item.license_number}`}
          closeAction={onClear}
          pictureURL={getUserImage(item.profile_picture)}
        />
      );
    }
    case OMNISEARCH_RESULT_TYPES.monitor: {
      return (
        <CloseableInputSearchResult
          title={`${item.first_name} ${item.last_name}`}
          subtitle="Monitor"
          closeAction={onClear}
          pictureURL={getUserImage(item.profile_picture)}
        />
      );
    }
    default: return null;
  }
};

const customRenderItem = (item: Object, isHighlighted: boolean) => (
  <OmnisearchResult
    {...item}
    isSelected={isHighlighted}
    size="small"
  />
);

const CreateRoutePopupForm = ({
  handleSubmit,
  fetchMonitors,
  fetchDrivers,
  fetchBuses,
}: CreateRoutePopupFormPropTypes) => {
  const WEEK_DAYS_OPTIONS = [
    { value: 0, label: i18n.t('weekdays.mon') },
    { value: 1, label: i18n.t('weekdays.tues') },
    { value: 2, label: i18n.t('weekdays.wed') },
    { value: 3, label: i18n.t('weekdays.thurs') },
    { value: 4, label: i18n.t('weekdays.fri') },
    { value: 5, label: i18n.t('weekdays.sat') },
    { value: 6, label: i18n.t('weekdays.sun') },
  ];

  const ROUTE_TYPE_OPTIONS = [
    { value: GOING_ROUTE_TYPE, label: i18n.t('routes.recolection') },
    { value: RETURNING_ROUTE_TYPE, label: i18n.t('routes.delivery') },
  ];

  return (
    <form id="createRoute" onSubmit={handleSubmit}>
      <PopupHeader>
        <PopupHeaderLeftGroup>
          <PopupHeaderTitle title={CreateButton(i18n.t('labels.route', { count: 1 }))} size="big" />
        </PopupHeaderLeftGroup>
        <PopupHeaderRightGroup>
          <PopupCloseButton
            size="medium"
            Icon={MdClose}
            tint="mediumGray"
            clearMargins
            isTransparent
            isOutlined
          />
        </PopupHeaderRightGroup>
      </PopupHeader>
      <PopupContent className={styles.createRoutePopupContent} size="large">
        <Grid>
          <Row>
            <Col xs={8}>
              <Field
                name="name"
                label={i18n.t('labels.name', { count: 1 })}
                placeholder="ie. Villa Nueva - San Cristóbal"
                component={RenderInput}
                type="text"
                autoFocus
              />
            </Col>
            <Col xs={4}>
              <Field
                name="route_type"
                label={i18n.t('labels.type')}
                component={RenderSelect}
                options={ROUTE_TYPE_OPTIONS}
              />
              <br />
            </Col>
          </Row>
          <Row>
            <Col xs={4}>
              <Field
                name="monitor"
                label={i18n.t('labels.monitor', { count: 1 })}
                component={RenderTypeahead}
                inputDebounce={200}
                renderSelectedValue={customRenderSelected}
                renderLoading={() => <OmnisearchLoadingResults size="small" count={3} />}
                fetchItems={fetchMonitors}
                renderItem={customRenderItem}
                inputProps={{
                  Icon: MdSearch,
                  iconPosition: 'right',
                  placeholder: Search(i18n.t('labels.monitor', { count: 1 }), 1),
                }}
              />
            </Col>
            <Col xs={4}>
              <Field
                name="driver"
                label={i18n.t('labels.driver')}
                component={RenderTypeahead}
                inputDebounce={200}
                renderSelectedValue={customRenderSelected}
                renderLoading={() => <OmnisearchLoadingResults size="small" count={3} />}
                fetchItems={fetchDrivers}
                renderItem={customRenderItem}
                inputProps={{
                  Icon: MdSearch,
                  iconPosition: 'right',
                  placeholder: Search(i18n.t('labels.driver'), 1),
                }}
              />
            </Col>
            <Col xs={4}>
              <Field
                name="bus"
                label={i18n.t('common.bus', { count: 1 })}
                component={RenderTypeahead}
                inputDebounce={200}
                renderSelectedValue={customRenderSelected}
                renderLoading={() => <OmnisearchLoadingResults size="small" count={3} />}
                fetchItems={fetchBuses}
                renderItem={customRenderItem}
                inputProps={{
                  Icon: MdSearch,
                  iconPosition: 'right',
                  placeholder: Search(i18n.t('common.bus', { count: 1 }), 1),
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Field
                name="week_days"
                label={i18n.t('routes.routeTimeFrame')}
                component={RenderMultiSelect}
                options={WEEK_DAYS_OPTIONS}
                // disabled={DISABLED_WEEK_DAYS}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <Field
                name="expected_departure_time"
                label={i18n.t('routes.expectedDepartureTime')}
                component={RenderTimePicker}
                clearMargins
              />
            </Col>
            <Col xs={6}>
              <Field
                label={i18n.t('routes.expectedArrivalTime')}
                name="expected_completion_time"
                component={RenderTimePicker}
                clearMargins
              />
            </Col>
          </Row>
        </Grid>
      </PopupContent>
      <PopupFooter size="large">
        <PopupCloseButton title={Cancel()} tint="paleGray" />
        <Button type="submit" title={CreateButton(i18n.t('labels.route', { count: 1 }))} />
      </PopupFooter>
    </form>
  );
};

export default connect(
  (state, { routeId }) => {
    const schoolId = (selectors.getProfileOfType(state, 'admin') || {}).school || -1;
    const token = selectors.getToken(state);
    const defaultWeekDays = [0, 1, 2, 3, 4];

    const fetchMonitors = generateFetcher(
      OMNISEARCH_RESULT_TYPES.monitor,
      schoolId,
      token,
    );
    const fetchDrivers = generateFetcher(
      OMNISEARCH_RESULT_TYPES.busDriver,
      schoolId,
      token,
    );
    const fetchBuses = generateFetcher(
      OMNISEARCH_RESULT_TYPES.bus,
      schoolId,
      token,
    );

    if (routeId) {
      const route = selectors.getRoute(state, routeId);
      const {
        name = '',
        week_days = defaultWeekDays,
        route_type = GOING_ROUTE_TYPE,
        monitor,
        expected_departure_time,
        expected_completion_time,
        driver,
        bus,
      } = route;
      const localDepTime = moment.utc(expected_departure_time, 'HH: mm').local().format('HH: mm A');
      const localCompTime = moment.utc(expected_completion_time, 'HH: mm').local().format('HH: mm A');
      return {
        initialValues: {
          name,
          week_days,
          expected_departure_time: localDepTime,
          expected_completion_time: localCompTime,
          route_type,
          monitor: {
            result_type: OMNISEARCH_RESULT_TYPES.monitor,
            ...monitor,
          },
          driver: {
            result_type: OMNISEARCH_RESULT_TYPES.busDriver,
            ...driver,
          },
          bus: {
            result_type: OMNISEARCH_RESULT_TYPES.bus,
            ...selectors.getBus(state, bus),
          },
          edit: true,
        },
        fetchDrivers,
        fetchMonitors,
        fetchBuses,
      };
    }

    return {
      initialValues: {
        week_days: defaultWeekDays,
        route_type: GOING_ROUTE_TYPE,
      },
      edit: false,
      fetchDrivers,
      fetchMonitors,
      fetchBuses,
    };
  },
  undefined,
)(reduxForm({
  form: 'createRoute',
  onSubmit,
  validate,
})(CreateRoutePopupForm));



// WEBPACK FOOTER //
// ./src/scripts/components/CreateRoutePopupForm/index.jsx